import React from 'react'
import { t } from 'i18next'
import { EquipmentCategory } from 'app/types/types'
import { ExpandableColumn, ExpandableSection } from '../components/organisms/ExpandableTable/ExpandableTable'
import { capitalizeIfEnglish } from './utils'
import { EquipmentByCategoryForPricingSummary, PropertyKeysForPricingSummaryCategory } from 'app/types/ui/pricing'

type EquipmentExpandResults<T extends EquipmentCategory> = {
  columns: Array<ExpandableColumn<EquipmentByCategoryForPricingSummary[T]> & { propertyKey: string }>
  sections: ExpandableSection[]
}

type ExpandableEquipmentFieldConfig = {
  [K in EquipmentCategory]: Array<{
    translationKey: string
    propertyKey: PropertyKeysForPricingSummaryCategory<K>
    isMainColumn?: boolean
    section?: string
    className?: string
    choiceTranslationKey?: string
  }>
}

const equipmentColumnsConfig: ExpandableEquipmentFieldConfig = {
  elevator: [
    // Main columns
    {
      translationKey: 'xlAssessmentWizard.equipment.koneEquipmentNumber',
      propertyKey: 'koneEquipmentNumber',
      isMainColumn: true
    },
    {
      translationKey: 'xlAssessmentWizard.equipment.inventoryNumber',
      propertyKey: 'inventoryNumber',
      isMainColumn: true
    },
    {
      translationKey: 'xlAssessmentWizard.elevator.technicalPlatform',
      propertyKey: 'technicalPlatform',
      isMainColumn: true,
      choiceTranslationKey: 'assessmentQuestions:technicalPlatform.choices'
    },
    {
      translationKey: 'xlAssessmentWizard.equipment.manufacturer',
      propertyKey: 'manufacturer',
      isMainColumn: true
    },
    {
      translationKey: '',
      propertyKey: 'priceString',
      className: 'font-bold',
      isMainColumn: true
    },
    {
      translationKey: 'pricingIndex.tabs.price',
      propertyKey: 'discountedPrice',
      className: 'font-bold',
      isMainColumn: true
    },
    // Equipment details
    { translationKey: 'xlAssessmentWizard.equipment.year', propertyKey: 'year', section: 'equipmentDetails' },
    {
      translationKey: 'xlAssessmentWizard.elevator.ratedLoad',
      propertyKey: 'ratedLoad',
      section: 'equipmentDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.equipment.manufacturer',
      propertyKey: 'manufacturer',
      section: 'equipmentDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.elevator.additionalDoors',
      propertyKey: 'additionalDoors',
      section: 'equipmentDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.equipment.operationalEnvironment',
      propertyKey: 'operationalEnvironment',
      section: 'equipmentDetails',
      choiceTranslationKey: 'assessmentQuestions:operationalEnvironment.choices'
    },
    {
      translationKey: 'xlAssessmentWizard.elevator.doorsCount',
      propertyKey: 'doorsCount',
      section: 'equipmentDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.elevator.technicalPlatform',
      propertyKey: 'technicalPlatform',
      section: 'equipmentDetails',
      choiceTranslationKey: 'assessmentQuestions:technicalPlatform.choices'
    },
    {
      translationKey: 'xlAssessmentWizard.elevator.ratedSpeed',
      propertyKey: 'ratedSpeed',
      section: 'equipmentDetails'
    },

    // Optional details
    {
      translationKey: 'xlAssessmentWizard.equipment.equipmentAddress',
      propertyKey: 'equipmentAddress',
      section: 'optionalDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.equipment.APFComponent',
      propertyKey: 'apfComponent',
      section: 'optionalDetails',
      choiceTranslationKey: 'assessmentQuestions:apfComponent.choices'
    },
    {
      translationKey: 'xlAssessmentWizard.equipment.koneConnectivityDeviceType',
      propertyKey: 'koneConnectivityDeviceType',
      section: 'optionalDetails',
      choiceTranslationKey: 'assessmentQuestions:koneConnectivityDeviceType.choices'
    },
    {
      translationKey: 'xlAssessmentWizard.equipment.manufacturingSerialnumber',
      propertyKey: 'manufacturingSerialnumber',
      section: 'optionalDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.equipment.equipmentType',
      propertyKey: 'equipmentType',
      section: 'optionalDetails',
      choiceTranslationKey: 'assessmentQuestions:equipmentType.choices'
    },

    // Location details
    {
      translationKey: 'xlAssessmentWizard.building.name',
      propertyKey: 'locationName',
      section: 'locationDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.building.streetAddress',
      propertyKey: 'locationAddress',
      section: 'locationDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.building.city',
      propertyKey: 'locationCity',
      section: 'locationDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.building.country',
      propertyKey: 'locationCountry',
      section: 'locationDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.building.postalCode',
      propertyKey: 'locationPostalCode',
      section: 'locationDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.building.region',
      propertyKey: 'locationRegion',
      section: 'locationDetails'
    }
  ],

  escalator: [
    // Main columns
    {
      translationKey: 'xlAssessmentWizard.equipment.koneEquipmentNumber',
      propertyKey: 'koneEquipmentNumber',
      isMainColumn: true
    },
    {
      translationKey: 'xlAssessmentWizard.equipment.inventoryNumber',
      propertyKey: 'inventoryNumber',
      isMainColumn: true
    },
    {
      translationKey: 'xlAssessmentWizard.equipment.manufacturer',
      propertyKey: 'manufacturer',
      isMainColumn: true
    },
    {
      translationKey: 'xlAssessmentWizard.equipment.operationalEnvironment',
      propertyKey: 'operationalEnvironment',
      isMainColumn: true,
      choiceTranslationKey: 'assessmentQuestions:operationalEnvironment.choices'
    },
    {
      translationKey: '',
      propertyKey: 'priceString',
      className: 'font-bold',
      isMainColumn: true
    },
    {
      translationKey: 'pricingIndex.tabs.price',
      propertyKey: 'discountedPrice',
      className: 'font-bold',
      isMainColumn: true
    },

    // Equipment details
    { translationKey: 'xlAssessmentWizard.equipment.year', propertyKey: 'year', section: 'equipmentDetails' },
    {
      translationKey: 'xlAssessmentWizard.escalator.escalatorType',
      propertyKey: 'escalatorType',
      section: 'equipmentDetails',
      choiceTranslationKey: 'assessmentQuestions:escalatorType.choices'
    },
    { translationKey: 'xlAssessmentWizard.escalator.length', propertyKey: 'length', section: 'equipmentDetails' },
    {
      translationKey: 'xlAssessmentWizard.equipment.operationalEnvironment',
      propertyKey: 'operationalEnvironment',
      section: 'equipmentDetails',
      choiceTranslationKey: 'assessmentQuestions:operationalEnvironment.choices'
    },
    {
      translationKey: 'xlAssessmentWizard.escalator.operationalEnvironmentCondition',
      propertyKey: 'operationalEnvironmentCondition',
      section: 'equipmentDetails',
      choiceTranslationKey: 'assessmentQuestions:operationalEnvironmentCondition.choices'
    },
    {
      translationKey: 'xlAssessmentWizard.escalator.numberOfDrives',
      propertyKey: 'numberOfDrives',
      section: 'equipmentDetails',
      choiceTranslationKey: 'assessmentQuestions:numberOfDrives.choices'
    },
    { translationKey: 'xlAssessmentWizard.escalator.rise', propertyKey: 'rise', section: 'equipmentDetails' },

    // Optional details
    {
      translationKey: 'xlAssessmentWizard.equipment.equipmentAddress',
      propertyKey: 'equipmentAddress',
      section: 'optionalDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.escalator.escalatorGearbox',
      propertyKey: 'escalatorGearbox',
      section: 'optionalDetails',
      choiceTranslationKey: 'assessmentQuestions:escalatorGearbox.choices'
    },
    {
      translationKey: 'xlAssessmentWizard.escalator.operationMode',
      propertyKey: 'operationMode',
      section: 'optionalDetails',
      choiceTranslationKey: 'assessmentQuestions:operationMode.choices'
    },
    {
      translationKey: 'xlAssessmentWizard.equipment.manufacturingSerialnumber',
      propertyKey: 'manufacturingSerialnumber',
      section: 'optionalDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.equipment.equipmentType',
      propertyKey: 'equipmentType',
      section: 'optionalDetails',
      choiceTranslationKey: 'assessmentQuestions:equipmentType.choices'
    },

    // Location details
    {
      translationKey: 'xlAssessmentWizard.building.name',
      propertyKey: 'locationName',
      section: 'locationDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.building.streetAddress',
      propertyKey: 'locationAddress',
      section: 'locationDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.building.city',
      propertyKey: 'locationCity',
      section: 'locationDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.building.country',
      propertyKey: 'locationCountry',
      section: 'locationDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.building.postalCode',
      propertyKey: 'locationPostalCode',
      section: 'locationDetails'
    },
    {
      translationKey: 'xlAssessmentWizard.building.region',
      propertyKey: 'locationRegion',
      section: 'locationDetails'
    }
  ]
}

const sectionMeta: Array<{ sectionDetails: string; title: string; layout: 'full' | 'half'; order: number }> = [
  {
    title: 'prepare.refresh.equipmentDetailSection',
    layout: 'full',
    order: 1,
    sectionDetails: 'equipmentDetails'
  },
  {
    title: 'prepare.refresh.optionalDetailSection',
    layout: 'half',
    order: 2,
    sectionDetails: 'optionalDetails'
  },
  { title: 'prepare.refresh.locationDetailSection', layout: 'half', order: 3, sectionDetails: 'locationDetails' }
]

/**
 * Returns ExpandableSection[] for a given EquipmentCategory (elevator, escalator, etc.)
 * Uses equipmentColumnsConfig to map sections and columns dynamically.
 */
export function getSectionsByCategory(category: EquipmentCategory): ExpandableSection[] {
  // Section metadata for each category

  const columnFields = equipmentColumnsConfig[category] || []

  const result = sectionMeta.map(section => {
    const obj: ExpandableSection = {
      title: t(section.title),
      layout: section.layout,
      order: section.order,

      columns: [
        ...columnFields
          .filter(column => column.section === section.sectionDetails)
          .map(column => capitalizeIfEnglish(t(column.translationKey)))
      ]
    }
    return obj
  })

  return result
}

function getAccessor<T extends EquipmentCategory>(
  key: string,
  renderCell: (value: string, className?: string, key?: string, id?: string) => React.ReactNode,
  choiceTranslationKey?: string,
  className?: string
): (row: EquipmentByCategoryForPricingSummary[T]) => React.ReactNode {
  return (row: EquipmentByCategoryForPricingSummary[T]) => {
    const value =
      choiceTranslationKey && row[key as keyof typeof row]
        ? t(`${choiceTranslationKey}.${row[key as keyof typeof row]}`, {
            defaultValue: String(row[key as keyof typeof row])
          })
        : row[key as keyof typeof row]
          ? row[key as keyof typeof row]
          : '-'

    return renderCell(value && value !== 'null' ? String(value) : '-', className, key, row.salesToolId)
  }
}

export function getColumnsConfigByCategory<T extends EquipmentCategory>(
  equipment: EquipmentByCategoryForPricingSummary[T] | null,
  renderCell: (value: string, className?: string, key?: string, id?: string) => React.ReactNode
): EquipmentExpandResults<T> {
  if (!equipment || !equipment.equipmentCategory) {
    return {} as EquipmentExpandResults<T>
  }

  const fieldsConfig = equipmentColumnsConfig[equipment.equipmentCategory]

  if (!fieldsConfig) {
    return {} as EquipmentExpandResults<T>
  }
  const columns: Array<ExpandableColumn<EquipmentByCategoryForPricingSummary[T]> & { propertyKey: string }> =
    fieldsConfig.map(config => {
      const { translationKey, isMainColumn, section, className, propertyKey, choiceTranslationKey } = config

      return {
        header: capitalizeIfEnglish(t(translationKey)),
        accessor: getAccessor(propertyKey, renderCell, choiceTranslationKey, className),
        isMainColumn,
        section,
        propertyKey,
        className
      }
    })

  const sections: ExpandableSection[] = getSectionsByCategory(equipment.equipmentCategory)

  return { columns, sections }
}
